import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { useMsal } from "@azure/msal-react";

import { RootState } from '../../../../redux/application/redux-store';
import { Ticket, TicketAttachmentFile, TicketChangeLog } from '../../../../api-client';
import { IHookCallback } from '../../../../common/models/IHookCallback';
import { TicketApiClient } from '../../../../providers/api-provider';
import { NotificationHelper } from '../../../../utils/notification';
import { clearAttachments } from '../../../../redux/slices/Ticket/ticketSlice';
import { useTranslation } from 'react-i18next';
import helpers from '../../../../utils/helpers/helpers';

export const useTicketDetails = () => {
    const {
        instance
    } = useMsal();

    const {
        t: translate
    } = useTranslation();

    const dispatch = useDispatch();

    const iaiAttachments = useSelector((state: RootState) => state.ticket.attachments);

    interface ICreateTicket extends IHookCallback {
        ticket: Ticket;
    }

    const [createTicketLoading, setCreateTicketLoading] = useState<boolean>(false);

    const handleCreateTicket = async (params: ICreateTicket) => {
        setCreateTicketLoading(true);

        const clientApi = await TicketApiClient(instance, helpers.getStoredI18Lang());

        clientApi.insertTicket(params.ticket)
            .then((response) => {
                if (iaiAttachments && !!iaiAttachments.length) {
                    _.each(iaiAttachments, entry => {
                        _handleUploadTicketAttachment({
                            id: response.data.id!,
                            attachment: entry
                        });
                    });

                    dispatch(clearAttachments());
                }

                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    message: translate('system.notification.insertTicketSuccess.title'),
                    title: translate('system.notification.insertTicketSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.insertTicket.title'),
                    title: translate('system.notification.insertTicket.message')
                });
            })
            .finally(() => {
                setCreateTicketLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    interface IRetrieveTicket extends IHookCallback {
        id: string;
        onRetrieveSuccess?: (ticket: Ticket) => void;
    }

    const [retrieveTicket, setRetrieveTicket] = useState<Ticket>();
    const [retrieveTicketLoading, setRetrieveTicketLoading] = useState<boolean>(false);

    const handleRetrieveTicket = async (params: IRetrieveTicket) => {
        setRetrieveTicketLoading(true);

        const clientApi = await TicketApiClient(instance, helpers.getStoredI18Lang());

        clientApi.getTicket(params.id)
            .then((response: any) => {
                setRetrieveTicket(response.data);

                params.onRetrieveSuccess && params.onRetrieveSuccess(response.data);
            })
            .catch((reason) => {
                setRetrieveTicket(undefined);

                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.getTicket.title'),
                    title: translate('system.notification.getTicket.message')
                });
            })
            .finally(() => {
                setRetrieveTicketLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    interface IUpdateTicket extends IHookCallback {
        id: string;
        ticket: Ticket;
    }

    const [updateTicketLoading, setUpdateTicketLoading] = useState<boolean>(false);

    const handleUpdateTicket = async (params: IUpdateTicket) => {
        setUpdateTicketLoading(true);

        const clientApi = await TicketApiClient(instance, helpers.getStoredI18Lang());

        clientApi.updateTicket(params.id, params.ticket)
            .then((response) => {
                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    message: translate('system.notification.updateTicketSuccess.title'),
                    title: translate('system.notification.updateTicketSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.updateTicket.title'),
                    title: translate('system.notification.updateTicket.message')
                });
            })
            .finally(() => {
                setUpdateTicketLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    interface IUploadTicketAttachment extends IHookCallback {
        id: string;
        attachment: TicketAttachmentFile;
    }

    const _handleUploadTicketAttachment = async (params: IUploadTicketAttachment) => {
        const clientApi = await TicketApiClient(instance, helpers.getStoredI18Lang());

        clientApi.insertTicketAttachment(params.id, params.attachment)
            .then((response) => {
                params.onSuccess && params.onSuccess();

                NotificationHelper.createNotification({
                    type: 'success',
                    message: translate('system.notification.insertDetailTicketAttachmentSuccess.title'),
                    title: translate('system.notification.insertDetailTicketAttachmentSuccess.message')
                });
            })
            .catch((reason) => {
                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.insertDetailTicketAttachment.title'),
                    title: translate('system.notification.insertDetailTicketAttachment.message')
                });
            })
            .finally(() => {
                params.onAlways && params.onAlways();
            });
    }

    interface IRetrieveChangeLogs extends IHookCallback {
        id: string;
        onRetrieveSuccess?: (changeLogs: TicketChangeLog[]) => void;
    }

    const [retrieveChangeLogs, setRetrieveChangeLogs] = useState<TicketChangeLog[]>();
    const [retrieveChangeLogsLoading, setRetrieveChangeLogsLoading] = useState<boolean>(false);

    const handleRetrieveChangeLogs = async (params: IRetrieveChangeLogs) => {
        setRetrieveChangeLogsLoading(true);

        const clientApi = await TicketApiClient(instance, helpers.getStoredI18Lang());

        clientApi.getTicketChangeLogs(params.id)
            .then((response) => {
                setRetrieveChangeLogs(response.data);

                params.onRetrieveSuccess && params.onRetrieveSuccess(response.data);
            })
            .catch((reason) => {
                setRetrieveChangeLogs([]);

                params.onError && params.onError();

                NotificationHelper.createNotification({
                    type: 'error',
                    message: translate('system.notification.getTicketChangeLogs.title'),
                    title: translate('system.notification.getTicketChangeLogs.message')
                });
            })
            .finally(() => {
                setRetrieveChangeLogsLoading(false);

                params.onAlways && params.onAlways();
            });
    }

    return {
        handleCreateTicket,
        createTicketLoading,
        handleRetrieveTicket,
        retrieveTicket,
        retrieveTicketLoading,
        handleUpdateTicket,
        updateTicketLoading,
        handleRetrieveChangeLogs,
        retrieveChangeLogs,
        retrieveChangeLogsLoading
    }
}